import {Box, Button, Stack} from "@mui/material";
import * as React from 'react'
import {addDeviceRequest, getARPOutput} from "../../../scripts/api_v2/commands_client";
import {Title} from "../../../components/general/typography";
import useBackend, {useUserPrivilegeKeyPermissionsMap} from "../../../hooks/use_backend";
import Loader from "../../../components/general/loader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AutoProbedCard } from "./auto_probed_card";
import { ARPCard } from "../../../components/device/arp_card";
import { AddDiscoveredStepper } from "../../../components/device/add/add_discovered_stepper";
import { useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { fetchSiteProperties } from "../../../scripts/api_v2/properties_client";
import { AddDeviceStepper } from "../../../components/device/add/add_device_stepper";
import { ProbeDeviceModal } from "../../../components/device/probe/probe_device_modal";
import { DeviceCard } from "../../../components/device/device_card";
import { DeviceDialog } from "../../../components/device/device_dialog";
import { enqueueSnackbar } from "notistack";
import { isComponentDisabledForUpdates } from "../../login/login_utils";
import { isBlockedForCommissioning } from "../../../scripts/utils";
import { GPCCard } from "../../../components/general_tab/gpc_card";

const devTemplate = { device_type: "", name: "", device_class: "" };

const DEVICE_CONFIG_COMPARE_KEYS = [
  "minimum_soc",
  "maximum_soc",
  "battery_tracking_mode",
  "phase_configuration",
  "direction",
  "curtailment_enabled",
  "battery_tracking_enabled",
  "enabled",
  "inverter_power",
  "capacity",
  "charge_rate",
];

const DEVICE_CONFIG_PROPERTY_KEY_OVERRIDES = {
  minimum_soc: "hard_minimum_soc",
};

function valuesDiffer(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) !== String(b);
}

function hasDeviceConfigurationMismatch(device, deviceProperties) {
  if (!device || !deviceProperties) return false;

  for (const key of DEVICE_CONFIG_COMPARE_KEYS) {
    const propertyKey = DEVICE_CONFIG_PROPERTY_KEY_OVERRIDES[key] || key;
    if (key in device && propertyKey in deviceProperties && valuesDiffer(device[key], deviceProperties[propertyKey])) {
      return true;
    }
  }

  for (const key of Object.keys(deviceProperties)) {
    if (!key.startsWith("conf_")) continue;
    const suffix = key.slice(5);
    const appliedKey = `act_${suffix}`;
    if (appliedKey in deviceProperties && valuesDiffer(deviceProperties[key], deviceProperties[appliedKey])) {
      return true;
    }
  }

  return false;
}

export function setDevice(device, queryClient, siteId, backendFetch) {
    queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backendFetch, siteId], (prev) => {
        const updated = {...prev.devices};
        updated[device.device_id] = device;
        return {...prev, "devices": updated}
    })
}

export function removeDevice(device, queryClient, siteId, backendFetch) {
    queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backendFetch, siteId], (prev) => {
        const updatedDevices = {...prev.devices};
        delete updatedDevices[device.device_id];
        return {...prev, "devices": updatedDevices}
    })
}

export default function DevicesTab({ required_privilege }) {
  const [addDeviceOpen, setAddDeviceOpen] = React.useState(false);
  const [probeDeviceOpen, setProbeDeviceOpen] = React.useState(false);
  const [addDiscoverOpen, setAddDiscoverOpen] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState({});
  const [selectedDiscovered, setSelectedDiscovered] = React.useState(devTemplate);
  const [selectedAutoProbed, setSelectedAutoProbed] = React.useState(null);
  const [deviceDialogOpen, setDeviceDialogOpen] = React.useState(false);
  const [showARP, setShowARP] = React.useState(false);
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const params = useParams();
  const location = useLocation();
  const backend = useBackend();
  const queryClient = useQueryClient();

  let {
    data: siteData,
    error,
    isLoading,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  let {
    data: ARPData,
    isLoading: ARPLoading,
    refetch: refetchArp,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.FETCH_ARP, backend.fetch, params.site_id],
    queryFn: () => getARPOutput(backend.fetch, params.site_id),
    enabled: false,
  });

  let { data: siteProps } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PROPERTIES, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProperties(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

const mismatchByDeviceId = React.useMemo(() => {
    if (!siteData?.devices || !siteProps?.device_properties) return {};

    const mismatches = {};
    Object.values(siteData.devices).forEach((device) => {
      mismatches[device.device_id] = hasDeviceConfigurationMismatch(
        device,
        siteProps.device_properties[device.device_id],
      );
    });

    return mismatches;
  }, [siteData, siteProps]);

  // Open device dialog when navigated from alerts tab
  React.useEffect(() => {
    const targetId = location.state?.openDeviceId;
    if (!targetId || !siteData?.devices) return;
    const device = Object.values(siteData.devices).find((d) => d.device_id === targetId);
    if (device) {
      setSelectedDevice(device);
      setDeviceDialogOpen(true);
      window.history.replaceState({}, ''); // clear state so back-navigation doesn't re-open
    }
  }, [siteData, location.state?.openDeviceId]);

    function arpCleaner() {
        if (!ARPData) return []
        return Object.values(ARPData).sort((a, b) => {
            if (a.device_type.toLowerCase().includes("unknown")) {
                return 1
            } else if (b.device_type.toLowerCase().includes("unknown")) {
                return -1
            } else {
                return a.device_type.localeCompare(b.device_type)
            }
        })
    }

    const cleanArpData = React.useMemo(arpCleaner, [ARPData])

    async function handleARPShow() {
        if (!showARP) {
            refetchArp()
        }
        setShowARP((prev) => !prev)
    }

    async function handleAddDevice(device) {
        addDeviceMutation({
            device: device,
            discovered: false
        })
    }

    async function handleAddDiscovered(device) {
        addDeviceMutation({
            device: device,
            discovered: true
        })
    }

    const {mutate: addDeviceMutation, isPending: isAddDeviceMutating} = useMutation({
        mutationFn: async ({device, discovered}) => {
            return await addDeviceRequest(backend.fetch, params.site_id, device)
        },
        onMutate: async (data) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id]})
            // Ideally we would optimistically update to the new value
        },
        onError: (err, variables, context) => {
            enqueueSnackbar("Device add failed", {variant: 'error'})
        },
        onSuccess: async (data, variables) => {
            const {discovered} = variables

            queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
                if (discovered) {
                    const updated = {...prev.devices};
                    updated[data.device_id] = data;
                    const updatedDiscovered = {...prev.discovered};
                    delete updatedDiscovered[data.device_id];
                    return {...prev, "devices": updated, "discovered": updatedDiscovered}
                } else {
                    const updated = {...prev.devices};
                    updated[data.device_id] = data;
                    return {...prev, "devices": updated}
                }
            })
            // Ideally we would refetch here instead of combining a post and get in one call
            //await refetch()
        },
        onSettled: (data, error, variables, context) => {
            const {discovered} = variables
            if (discovered) {
                setAddDiscoverOpen(false)
            } else {
                setAddDeviceOpen(false)
            }
        }
    });

    function AutoProbed({auto_probed}) {
        if (!auto_probed || auto_probed.length === 0) return null
        return <>
            <Box mb={1} mt={2}>
                <b>AUTO PROBED</b>
            </Box>
            {auto_probed.map((obj, id) => {
                if (!obj.existing_device) {
                    return <AutoProbedCard
                        key={id}
                        device={obj.device}
                        success={obj.success}
                        handleOpen={() => {
                            setSelectedAutoProbed(obj);
                            setProbeDeviceOpen(true);
                        }}
                    />
                }
            })}
        </>
    }

    return siteData ?
        <div>
            <Stack direction="row" spacing={2}>
                {/* Configured Devices */}
                <Box width="75%">
                    <Box mb={1} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <b>CONFIGURED</b>
                    </Box>
                    {siteData.devices ? Object.values(siteData.devices).map(obj => (
                        <DeviceCard
                            key={obj.device_id}
                            device={obj}
                            hasConfigurationMismatch={Boolean(mismatchByDeviceId[obj.device_id])}
                            handleOpen={() => {
                                setSelectedDevice(obj);
                                setDeviceDialogOpen(true);
                            }}
                        />
                    )) : <Title ml={5}>No devices configured yet</Title>}


                        <Box mb={1} mt={5} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                            <b>SETTINGS</b>
                        </Box>
                        <GPCCard display="flex" justifyContent="center" alignContent="center" permissionMap={permissionMap}
                        required_privilege={required_privilege}/>
                </Box>
                <Box width="25%" display="flex" flexDirection="column" alignContent="left">
                    {/* Discovered Devices */}
          
                    <Button
                        color={"secondary"}
                        onClick={() => {
                            setSelectedAutoProbed(null);
                            setProbeDeviceOpen(true)
                        }}
                        disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
                        sx={{marginTop: 2, marginLeft: 2, width: '80%', alignSelf: 'left'}}
                        variant="contained"
                    >
                        Add device
                    </Button>
                    <AutoProbed auto_probed={siteData.devices_general?.auto_probed}/>
                    {/* ARP Results */}
                    <Box mb={1} mt={3}
                         sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: "pointer"}}
                         onClick={handleARPShow}>
                        {showARP ? <ExpandMoreIcon/> : <KeyboardArrowRightIcon/>}
                        <b>ARP Results</b>
                    </Box>
                    {showARP && <Box display="flex" flexDirection="column" maxHeight="calc(100vh - 20rem)">
                        {ARPLoading ? <Loader/> : cleanArpData.map((el, index) => <ARPCard
                            key={index} device={el}/>)}
                    </Box>
                    }
                </Box>
            </Stack>

            <DeviceDialog open={deviceDialogOpen} device={selectedDevice}
                          setDevice={(dev) => setDevice(dev, queryClient, params.site_id, backend.fetch)}
                          removeDevice={(dev) => removeDevice(dev, queryClient, params.site_id, backend.fetch)}
                          closeModal={() => {
                              setDeviceDialogOpen(false)
                          }}
                          permissionMap={permissionMap}
                          required_privilege={required_privilege}/>
            {isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey()) || isBlockedForCommissioning(siteData) ? null :
                <AddDeviceStepper open={addDeviceOpen} setOpen={setAddDeviceOpen} handleAddDevice={handleAddDevice}/>
            }
            {isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey()) || isBlockedForCommissioning(siteData) ? null :
                <AddDiscoveredStepper device={selectedDiscovered} setDevice={setSelectedDiscovered}
                                      open={addDiscoverOpen}
                                      setOpen={setAddDiscoverOpen} handleAddDevice={handleAddDiscovered}/>
            }
            <ProbeDeviceModal open={probeDeviceOpen} setOpen={setProbeDeviceOpen} autoProbeResult={selectedAutoProbed}
                              setAutoProbeResult={setSelectedAutoProbed}/>
        </div>
        :
        isLoading ? <Loader fill={false}/> :
            error ?
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                    <div>Error loading data</div>
                    ;
                </Box> : null
}